/* istanbul ignore file */
import { splitLocale } from "@phn/entities/locale";
import { LatLong, Prediction } from "@phn/entities/places";
import { isString } from "@phn/utility/helper";
import windowService from "@phn/services/window-service";
import {
  NaviErrorTypes,
  NaviError,
  NaviErrorType,
} from "@phn/utility/navi-error";
import { getRegionSpecificCountry } from "./dealer/dealer-search-helper";

export class MapsService {
  // @ts-ignore
  place: google.maps.places.Place | null = null;

  // @ts-ignore
  private geocoderService: google.maps.Geocoder | null = null;

  // @ts-ignore
  private getLocation: ((latLong: LatLong) => google.maps.LatLng) | null = null;

  async init(apiKey: string): Promise<void> {
    try {
      const google = await windowService.fetchGoogleMapsApi(apiKey);
      await this.waitForMaps();
      this.place = new google.maps.places.Place({ id: "mapsService" });
      this.geocoderService = new google.maps.Geocoder();
      this.getLocation = (latLong: LatLong) =>
        new google.maps.LatLng(latLong.latitude, latLong.longitude);
    } catch (err) {
      throw new NaviError(
        // eslint-disable-next-line
        `windowService.fetchGoogleMapsApi failed with error: ${err}`,
        NaviErrorTypes.GENERAL,
      );
    }
  }

  async waitForMaps(): Promise<boolean> {
    const hasMaps = !!google.maps.places;
    if (hasMaps) return hasMaps;

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    await new Promise((resolve) => setTimeout(resolve, 30));
    return this.waitForMaps();
  }

  async getPredictions(input: string, locale: string): Promise<Prediction[]> {
    if (input.length === 0) {
      return [];
    }
    // ToDo: Check what todo if country is no valid iso-code
    // @ts-ignore
    const predictions = await new Promise<
      ReturnType<
        typeof google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions
      >
    >((resolve) => {
      try {
        const suggestions =
          google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            {
              includedRegionCodes: [
                getRegionSpecificCountry(splitLocale(locale).country),
              ],
              input,
            },
          );
        resolve(suggestions);
      } catch {
        throw new NaviError(
          `autocompleteSuggestion.getPlacePredictions failed.}`,
          NaviErrorTypes.GENERAL,
        );
      }
    });

    return (
      predictions?.suggestions
        ?.filter(
          ({ placePrediction }) =>
            isString(placePrediction?.placeId) &&
            isString(placePrediction?.text.text),
        )
        .map(({ placePrediction }) => {
          return {
            id: placePrediction!.placeId,
            text: placePrediction!.text.text,
          };
        }) ?? []
    );
  }

  async getPlaceCoordinates(): Promise<LatLong> {
    // @ts-ignore
    const response = await new Promise<{
      place: google.maps.places.Place;
    } | null>((resolve, reject) => {
      if (this.place === null) {
        reject(
          new NaviError("place is not initialised", NaviErrorTypes.GENERAL),
        );
        return;
      }

      try {
        const results = this.place.fetchFields({
          fields: ["location"],
        });

        resolve(results);
      } catch {
        reject(
          new NaviError(
            `placeService.fetchFields failed.`,
            NaviErrorTypes.GENERAL,
          ),
        );
      }
    });

    const latitude = response?.place.location?.lat();
    const longitude = response?.place.location?.lng();

    if (typeof latitude !== "number" || typeof longitude !== "number") {
      throw new NaviError(
        `placesService.fetchFields did not find latitude or longitude for placeId: ${this.place?.id}`,
        NaviErrorTypes.NO_RESULTS,
      );
    }

    return { latitude, longitude };
  }

  async getFormattedAddressFromCoordinates(latLong: LatLong): Promise<string> {
    if (this.getLocation === null) {
      throw new NaviError(
        "getLocation is not initialised",
        NaviErrorTypes.GENERAL,
      );
    }

    const location = this.getLocation(latLong);

    // @ts-ignore
    const response = await new Promise<google.maps.GeocoderResult[] | null>(
      (resolve, reject) => {
        if (this.geocoderService === null) {
          reject(
            new NaviError(
              "geocoderService is not initialised",
              NaviErrorTypes.GENERAL,
            ),
          );
          return;
        }

        this.geocoderService.geocode({ location }, (result, status) => {
          // eslint-disable-next-line
          status === "OK"
            ? resolve(result)
            : reject(
                new NaviError(
                  `geocoderService.geocode failed with status ${status}`,
                  getErrorType(status),
                ),
              );
        });
      },
    );

    if (response === null || response.length === 0) {
      throw new NaviError(
        "geocoderService.geocode did not find the location based on coordinates.",
        NaviErrorTypes.NO_RESULTS,
      );
    }

    return response[0].formatted_address;
  }
}

export function getErrorType(status: string): NaviErrorType {
  switch (status) {
    case "INVALID_REQUEST": {
      return NaviErrorTypes.INVALID_INPUT;
    }
    case "NOT_FOUND":
    case "ZERO_RESULTS": {
      return NaviErrorTypes.NO_RESULTS;
    }
    case "OVER_QUERY_LIMIT":
    case "UNKNOWN_ERROR": {
      return NaviErrorTypes.RETRY;
    }
    case "ERROR":
    case "REQUEST_DENIED":
    default: {
      return NaviErrorTypes.GENERAL;
    }
  }
}

export default new MapsService();
